import type { ReactNode } from 'react';

/**
 * V7-P4A shared field-grid primitive (the FIELDS-NOT-SENTENCES render law,
 * CLAUDE.md; registry component.field-grid -- built ONCE, generalizing the
 * page-local `.lead360-kv` dl idiom so labeled key/value facts stop being
 * re-invented per page). Micro-label over typed value, two responsive
 * columns on desktop, single-column stack under the narrow breakpoint
 * (mobile posture: fields stack cleanly, labels never collide with values).
 *
 * PRESENTATION-ONLY: values arrive fully resolved and typed from the
 * caller; nothing is fetched, derived, or mutated here. A missing value
 * renders the field's OWN honest fallback copy (pattern.honest-unavailable
 * -- never a bare dash, never an invented value).
 */

export interface FieldGridField {
  readonly label: string;
  /** Typed value node; null/undefined/'' renders the honest fallback. */
  readonly value?: ReactNode;
  /** Honest missing-value copy for THIS field (default 'Not recorded'). */
  readonly missingLabel?: string;
  /** data-testid stamped on the value cell (pins survive the retrofit). */
  readonly testId?: string;
}

export interface FieldGridProps {
  readonly fields: readonly FieldGridField[];
  /** 1 = compact stack (drawers/narrow hosts); 2 = responsive two-column. */
  readonly columns?: 1 | 2;
  /** Accessible group label. */
  readonly label?: string;
  readonly testId?: string;
}

function hasValue(value: ReactNode): boolean {
  return value !== undefined && value !== null && value !== '';
}

export default function FieldGrid({
  fields,
  columns = 2,
  label,
  testId,
}: FieldGridProps): JSX.Element {
  const cls =
    columns === 1 ? 'ac-fieldgrid ac-fieldgrid--cols1' : 'ac-fieldgrid';
  return (
    <dl className={cls} aria-label={label} data-testid={testId}>
      {fields.map((field) => (
        <div className="ac-fieldgrid__row" key={field.label}>
          <dt className="ac-fieldgrid__label">{field.label}</dt>
          {hasValue(field.value) ? (
            <dd className="ac-fieldgrid__value" data-testid={field.testId}>
              {field.value}
            </dd>
          ) : (
            <dd
              className="ac-fieldgrid__value ac-fieldgrid__value--missing"
              data-testid={field.testId}
            >
              {field.missingLabel ?? 'Not recorded'}
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}
